import React, { useMemo, useState, useEffect, useCallback } from 'react'
import DropdownFile from './File'
import { Icon, Feed, Grid, Image } from 'semantic-ui-react'
import { proxyGravatarUrl } from './utils/avatarProxy'

const DefaultAvatar = React.memo(() => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="#E0E1E2" />
    <circle cx="15" cy="12" r="5" fill="#A0A1A2" />
    <path d="M5 27.5C5 22 9.5 18 15 18C20.5 18 25 22 25 27.5" fill="#A0A1A2" />
  </svg>
));

const UserAvatar = React.memo(({ avatar }) => {
  const handleImageError = useMemo(() => (e) => {
    e.target.onerror = null;
    const fallbackAvatar = document.getElementById('avatar');
    if (fallbackAvatar?.src) {
      e.target.src = fallbackAvatar.src;
    }
  }, []);

  // Use our proxy utility to transform Gravatar URLs
  const proxiedAvatarUrl = useMemo(() => proxyGravatarUrl(avatar), [avatar]);

  return (
    <span style={{ display: 'inline-block', width: '40px', height: '40px', marginTop: '5px', position: 'relative' }}>
      <Image
        src={proxiedAvatarUrl}
        avatar
        loading='eager'
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        onError={handleImageError}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          minWidth: '30px',
          background: '#f0f0f0',
          position: 'absolute',
          top: 0,
          left: 0
        }}
      />
    </span>
  );
}, (prevProps, nextProps) => prevProps.avatar === nextProps.avatar);

const UserInfo = React.memo(({ creator, color, action_name, l, width }) => {
  // Use theme-aware color with fallback - provides balanced contrast with sidebar background
  const textColor = color || 'var(--muted-foreground)';
  const textStyle = { color: textColor, fontSize: 10, lineHeight: '1.4' };

  return (
    <Feed.Summary style={{ width }}>
      <Feed.User className='dcorange' id="creator_name" href={creator.url} target='_blank'>
        {creator.name}
      </Feed.User>
      {action_name !== 'assignment' &&
        <>
          <p style={{ margin: '2px 0' }}>
            <span style={textStyle}>Joined at </span>
            <span style={{ ...textStyle, fontWeight: '500' }}>
              {creator.created_at}
            </span>
          </p>
          {creator.class_room_name &&
            <p style={{ margin: '2px 0' }}>
              <span style={textStyle}>{l.classroom}: </span>
              <span style={{ ...textStyle, fontWeight: '500' }}>
                {creator.class_room_name}
              </span>
            </p>
          }
        </>
      }
    </Feed.Summary>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.creator.name === nextProps.creator.name &&
    prevProps.creator.url === nextProps.creator.url &&
    prevProps.creator.created_at === nextProps.creator.created_at &&
    prevProps.creator.class_room_name === nextProps.creator.class_room_name &&
    prevProps.color === nextProps.color &&
    prevProps.action_name === nextProps.action_name
  );
});

const UserName = React.memo((props) => (
  <Feed size='small' style={{ paddingTop: 0 }}>
    <Feed.Event>
      <Feed.Label>
        <UserAvatar avatar={props.creator.avatar} />
      </Feed.Label>
      <Feed.Content>
        <Grid>
          <Grid.Row verticalAlign="middle">
            <Grid.Column width={10}>
              <UserInfo
                creator={props.creator}
                color={props.color}
                action_name={props.action_name}
                l={l}
                width={props.width}
              />
            </Grid.Column>
            <Grid.Column width={6}>
              {props.userProjects?.length > 0 &&
                <DropdownFile
                  l={props.l}
                  userProjects={props.userProjects}
                  label='public'
                />
              }
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Feed.Content>
    </Feed.Event>
  </Feed>
), (prevProps, nextProps) => {
  return (
    prevProps.creator.id === nextProps.creator.id &&
    prevProps.creator.avatar === nextProps.creator.avatar &&
    prevProps.color === nextProps.color &&
    prevProps.width === nextProps.width &&
    JSON.stringify(prevProps.userProjects) === JSON.stringify(nextProps.userProjects)
  );
});

export default UserName;
